import { Deserializer } from 'jsonapi-serializer';
import { AccessTimePattern, AccessTimePatternWrapper } from '../models/AccessTimePattern';
import { AccessTimePatternName } from '../models/AccessTimePatternName'
import { buildDayList, getDays, convertToDuration, convertToTime, convertToEndTime } from "./utilities/dateFormatting";
import { customFilterBuilder } from "./utilities/customFilterBuilder";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { ListQuery } from '../models/ListQuery';
import { APIConnector } from './api/APIConnector';
import { PaginationModel } from '../models/Pagination';
import { Name } from '../models/Name';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { Days } from "../models/Day";
import { genericError } from '../models/GenericError';
import { INTERNAL_SERVER_ERROR } from '../constants/response-status-codes';
import { NO_CONTENT } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { DeleteEntityResult } from '../models/services/DeleteEntityResult';
import { stateTypeDirectTable, typeSystemStats } from '../constants/api';
import { Translator } from '../contexts/translator';

const apiConnector = new APIConnector();

export const storeAccessTimePattern = async (accessTimePattern: AccessTimePattern, createAccessTimePattern = true): Promise<StoreEntityResult> => {
    try {
        const newAccessTimePattern = {
            data: {
                type: "accessTimePattern",
                id: accessTimePattern.id,
                attributes: {
                    atpDefault: accessTimePattern.default ?? 0,
                    atpEndTime: accessTimePattern.endTimeExtract,
                    atpFri: accessTimePattern.dayList.includes(Days.friday),
                    atpHol: accessTimePattern.dayList.includes(Days.holidays),
                    atpMon: accessTimePattern.dayList.includes(Days.monday),
                    atpName: accessTimePattern.name,
                    atpSat: accessTimePattern.dayList.includes(Days.saturday),
                    atpStartTime: accessTimePattern.startTime,
                    atpSun: accessTimePattern.dayList.includes(Days.sunday),
                    atpThu: accessTimePattern.dayList.includes(Days.thursday),
                    atpTue: accessTimePattern.dayList.includes(Days.tuesday),
                    atpWed: accessTimePattern.dayList.includes(Days.wednesday)
                },
                relationships: {
                    site: {
                        data: {
                            type: "site",
                            id: accessTimePattern.siteId
                        }
                    }
                }
            }
        };

        const url = `accessTimePattern${createAccessTimePattern ? '' : `/${newAccessTimePattern.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createAccessTimePattern, newAccessTimePattern)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createAccessTimePattern) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, apiResult) => {
                        if (error) {
                            return reject({ status: response.status, id: newAccessTimePattern.data.id, success: false, message: genericError });
                        }
                        return resolve({ id: apiResult.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: accessTimePattern.id, success: true })
                }
 
                const json = await response.json();

                return reject({ status: response.status, id: newAccessTimePattern.data.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: newAccessTimePattern.data.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessTimePatternNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const accessTimePatternFields = 'fields[accessTimePattern]=atpName,isDefault,site';
        const siteFields = 'fields[site]=siteName';
        let url = `accessTimePattern?${paginationString}&include=site&${accessTimePatternFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ statusCode: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiAccessTimePattern: any[]) => {
                if (err) {
                    return reject({ statusCode: response.status, message: genericError });
                }

                const accessTimePattern: Name[] = apiAccessTimePattern.map((accessTimePattern): AccessTimePatternName => {
                    return {
                        value: accessTimePattern['id'],
                        label: accessTimePattern['atp-name'],
                        optionalColumns: [{ key: 'site', value: accessTimePattern['site']?.['site-name'] }],
                        isDefault: accessTimePattern['is-default']
                    };
                });

                return resolve({ rows: accessTimePattern, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            return reject({ statusCode: INTERNAL_SERVER_ERROR, message: genericError });
        })
    }
}

export const getAccessTimePatternById = async (id: string, translate: Translator): Promise<AccessTimePattern> => {
    try {
        const accessTimePatternFields = 'fields[accessTimePattern]=atpName,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol,atpStartTime,atpDuration,atpDefault,isDefault,site,totalAccessGroups,totalFloorGroups,totalOssGroups';
        const siteFields = 'fields[site]=siteName';
        const url = `accessTimePattern/${id}?include=site&${accessTimePatternFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ statusCode: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error, apiAccessTimePattern) => {
                if (error) {
                    return reject({ statusCode: response.status, message: genericError });
                }
                if (apiAccessTimePattern['id']) {
                    const accessTimePattern = buildAccessTimePattern(apiAccessTimePattern, translate);

                    return resolve(accessTimePattern);
                }

                return reject({ statusCode: response.status, message: genericError });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            return reject({ statusCode: INTERNAL_SERVER_ERROR, message: genericError });
        });
    }
}

export const getAccessTimePatternNames = async (listParameters?: ListQuery): Promise<{ rows: AccessTimePatternName[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let url = `accessTimePattern?${partialURL}&include=site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ statusCode: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiAccessTimePattern: any[]) => {
                if (err) {
                    return reject({ statusCode: response.status, message: genericError });
                }

                const accessTimePatternNames: AccessTimePatternName[] = apiAccessTimePattern.map((accessTimePattern): AccessTimePatternName => {
                    return {
                        value: accessTimePattern['id'],
                        label: accessTimePattern['atp-name'],
                        optionalColumns: [{ key: 'site', value: accessTimePattern['site']?.['site-name'] && accessTimePattern['site']?.['site-name'] }],
                        isDefault: accessTimePattern['is-default']
                    };
                });

                return resolve({ rows: accessTimePatternNames, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ statusCode: INTERNAL_SERVER_ERROR, message: genericError });
        })
    }
};

export const getAccessTimePatterns = async (translate: Translator, listParameters?: ListQuery): Promise<AccessTimePatternWrapper> => {
    try {
        const searchOnNames: string[] = ['atpMon', 'atpTue', 'atpWed', 'atpThu', 'atpFri', 'atpSat', 'atpSun', 'atpHol'];
        const withFilters = customFilterBuilder(searchOnNames, translate, listParameters?.filters, listParameters?.searchTerm);
        const fieldsAccessTimePattern = '&fields[accessTimePattern]=atpName,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol,atpStartTime,atpDuration,atpDefault,isDefault,site';
        const fieldsSite = '&fields[site]=siteName';
        const url = 'accessTimePattern?include=site' + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withFilters) + fieldsAccessTimePattern + fieldsSite;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiAccessTimePatterns: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const accessTimePatterns: AccessTimePattern[] = apiAccessTimePatterns.map((accessTimePattern) => buildAccessTimePattern(accessTimePattern, translate));

                return resolve({ rows: accessTimePatterns, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteAccessTimePattern = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const accessTimePattern = {
            data: {
                type: "accessTimePattern",
                id: id
            }
        }

        const url = `accessTimePattern/${id}`;
        const response = await apiConnector.deleteData(url, accessTimePattern)
    
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }
            const json = await response.json();
            return reject({ statusCode: response.status, id: id, success: false, status: response.status, message: getConstraintViolation(json.errors[0].detail)  });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

function buildAccessTimePattern(accessTimePattern: any, translate: Translator): AccessTimePattern {
    return {
        id: accessTimePattern['id'],
        name: accessTimePattern['atp-name'],
        days: getDays(
            accessTimePattern['atp-mon'], 
            accessTimePattern['atp-tue'], 
            accessTimePattern['atp-wed'], 
            accessTimePattern['atp-thu'],
            accessTimePattern['atp-fri'],
            accessTimePattern['atp-sat'], 
            accessTimePattern['atp-sun'], 
            accessTimePattern['atp-hol'], 
            translate),
        startTime: convertToTime((accessTimePattern['atp-start-time']).toString()),
        endTime: convertToEndTime((accessTimePattern['atp-start-time']).toString(), (accessTimePattern['atp-duration']).toString()),
        duration: convertToDuration((accessTimePattern['atp-duration']).toString()),
        site: accessTimePattern['site']['site-name'],
        siteId: accessTimePattern['site']['id'],
        dayList: buildDayList(
            accessTimePattern['atp-mon'],
            accessTimePattern['atp-tue'],
            accessTimePattern['atp-wed'],
            accessTimePattern['atp-thu'],
            accessTimePattern['atp-fri'],
            accessTimePattern['atp-sat'],
            accessTimePattern['atp-sun'],
            accessTimePattern['atp-hol'],
        ),
        default: accessTimePattern['atp-default'],
        isDefault: accessTimePattern['is-default'],
        accessGroups: accessTimePattern['total-access-groups'],
        floorGroups: accessTimePattern['total-floor-groups'],
        ossGroups: accessTimePattern['total-oss-groups'],
    };
}
